/*
      ___           ___
     /\  \         /\  \
    /::\  \       /::\  \
   /:/\:\  \     /:/\ \  \
  /::\ \:\  \   _\:\ \ \  \
 /:/\:\ \:\__\ /\ \:\ \ \__\
 \/_|::\/:/  / \:\ \:\ \/__/
    |:|::/  /   \:\ \:\__\
    |:|\/__/     \:\/:/  /
    |:|  |        \::/  /
     \|__|         \/__/

    Developed by Ralph Shippam - 2019.
*/

import {Injectable} from '@angular/core';

/*export enum Stage {
    all = RSValidateObjectProgress.all, // Use the inherent all from the checker
    start = 1,
    middle = 2,
    near end = 3,
    results = 4
}*/

export enum RSValidateObjectProgress {
    all = 0
}

export namespace RSValidator {

    export class Rules {
        public fields: Fields;
        public regex: FieldRegexs;
    }
    export interface Fields {
        [key: string]: Field;
    }
    export interface Field {
        type: string;
        min?: number | string;
        max?: number | string;
        regex?: FieldRegex;
        delim?: string;
        delim_limit?: number;
        decimals?: boolean;
        allowBlank?: boolean;
        allowedCharacters?: string;
        error?: string;
    }
    export interface FieldRegexs {
        [key: string]: FieldRegex;
    }
    export interface FieldRegex {
        pattern: string;
        flags?: string;
    }

    export class ErrorMember {
        error: string;
        section: number | string = null;
        scenarioSection: number = null; // TODO: to identify section, put the index for now?
        constructor(error: string, section?: number | string, scenarioSection?: number) {
            this.error = error;
            if (typeof section !== 'undefined') {
                this.section = section;
            }
            if (typeof scenarioSection !== 'undefined') {
                this.scenarioSection = scenarioSection;
            }
        }
    }
    export class ValidationMember {
        key: string;
        validator: any;
        validationStage: number;
        required: any | null;
        name: string;
        constructor(key: string, validator: any, validationStage: number, required?: any, name?: string) {
            this.key = key;
            this.validator = validator;
            this.validationStage = validationStage;
            if (required) {
                this.required = required;
            }
            if (name) {
                this.name = name;
            }
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class RSValidatorService {

    static rules: RSValidator.Rules;
    constructor() {
        RSValidatorService.rules = {} as RSValidator.Rules;
    }

    // Seek the right profile;
    private static getValidationProfile(name: string): RSValidator.Field {
        if (this.rules.fields.hasOwnProperty(name)) {
            const profile = {} as RSValidator.Field;
            Object.assign(profile, this.rules.fields[name]); // copy

            if (profile.type === 's') {
                profile.min = (profile.hasOwnProperty('min') ? profile.min : undefined);
                profile.max = (profile.hasOwnProperty('max') ? profile.max : undefined);

                // Extract regex pattern
                let pattern: string;
                let flags: string;
                if (profile.regex && typeof profile.regex === 'object') {
                    // Should be good!?
                    pattern = profile.regex.pattern;
                    flags = profile.regex.flags;
                } else if (typeof profile.regex === 'string') {
                    if (this.rules.regex.hasOwnProperty(profile.regex)) {
                        pattern = this.rules.regex[profile.regex].pattern;
                        flags = this.rules.regex[profile.regex].flags;
                    }
                }
                profile.regex = { pattern, flags } as RSValidator.FieldRegex;

                profile.allowedCharacters = (profile.hasOwnProperty('allowedCharacters') ? profile.allowedCharacters : undefined);
                profile.delim = (profile.hasOwnProperty('delim') ? profile.delim : undefined);
                profile.delim_limit = (profile.hasOwnProperty('delim_limit') ? profile.delim_limit : undefined);

            } else if (profile.type === 'n') {
                profile.min = (profile.hasOwnProperty('min') ? profile.min : undefined);
                profile.max = (profile.hasOwnProperty('max') ? profile.max : undefined);
                profile.decimals = (profile.hasOwnProperty('decimals') ? profile.decimals : undefined);
                profile.allowedCharacters = (profile.hasOwnProperty('allowedCharacters') ? profile.allowedCharacters : undefined);
                profile.allowBlank = (profile.hasOwnProperty('allowBlank') ? profile.allowBlank : undefined);
            }
            profile.error = profile.hasOwnProperty('error') ? profile.error : undefined;
            return profile;
        } else {
            return undefined;
        }
    }

    // Validate missing values;
    private static valueMissing = (value?, name?): string => {
        if (typeof value !== 'undefined' &&
            value !== null &&
            value !== 0 &&
            String(value).length !== 0) {
            return undefined;
        } else {
            return ((name && name !== 'Required') ? name : 'Input')  + ' is a required field. Please enter a valid input.';
        }
    };

    private static validateString = (inputString?, inputName?, minLength?,
                                     maxLength?, regexPattern?, regexFlags?,
                                     allowedCharacters?, delim?, delim_limit?,
                                     params?: object): string => {
        let error: string;
        if (typeof inputName === 'undefined' || inputName === null) { inputName = 'Input'; }
        if (typeof inputString === 'undefined' || inputString === null) { inputString = ''; }
        inputString = inputString.toString().trim();

        /* Max size is max of string? */
        if (!error && typeof minLength !== 'undefined') {
            if (inputString.length < minLength) {
                error = inputName + ' must be ' + (maxLength && maxLength !== minLength ? 'at least ' : '') + minLength + ' characters long.';
            }
            /* Bypass if we are 0 and allowed 0 */
            if (minLength === 0 && inputString.length === 0) {
                return error;
            }
        }

        if (!error && typeof maxLength !== 'undefined') {
            if (inputString.length > maxLength) {
                error = inputName + ' cannot be more than ' + maxLength + ' characters long.';
            }
        }

        let input_check = [];
        if (delim) {
            input_check = inputString.split(delim);
            if (typeof delim_limit !== 'undefined') {
                if (delim_limit < input_check.length) {
                    error = inputName + ' is only allowed ' + delim_limit + ' entries seperated by a \'' + delim + '\'.';
                }
            }
        } else {
            input_check = [inputString];
        }

        if (!error) {
            input_check.forEach((check) => {
                if (!error) {
                    if (!error && typeof allowedCharacters !== 'undefined') {
                        let niceName = '';
                        switch (allowedCharacters) {
                            case 'alpha':
                                allowedCharacters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
                                niceName = allowedCharacters + ' (a-Z)';
                                break;
                            case 'alphanumeric':
                                allowedCharacters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                                niceName = allowedCharacters + ' (a-Z or 0-9)';
                                break;
                            case 'numeric':
                                allowedCharacters = '0123456789';
                                niceName = allowedCharacters + ' (a-Z or 0-9)';
                                break;
                            default:
                                // Do nothing
                                niceName = '(' + allowedCharacters + ')';
                        }
                        const characters = allowedCharacters.split('');
                        let e = false;
                        check.split('').forEach(function (c) {
                            if (!e || characters.indexOf(c) === -1) { e = true; }
                        });

                        if (e) {
                            error = inputName + ' does not match the allowed input: ' + niceName;
                        }
                    }
                    if (!error && typeof regexPattern !== 'undefined') {
                        if (typeof regexFlags === 'undefined') { regexFlags = 'gi'; }
                        regexPattern = regexPattern.toString();

                        const re = new RegExp(regexPattern, regexFlags);
                        if (!re.test(check)) {
                            error = 'Please enter a valid ' + inputName + '. It does not appear to be correct.';
                        }
                    }
                }
            });
        }

        return error;
    };

    private static validateNumber(inputNumber?, inputName?, minNumber?,
                                  maxNumber?, allowDecimal?, allowedChars?,
                                  allowBlank?, params?: object): string {

        let error: string;
        if (typeof inputName === 'undefined' || inputName === null) { inputName = 'Input'; }
        if (typeof inputNumber === 'undefined' || inputNumber === null || inputNumber === '' || inputNumber.toString().length === 0) {
            if (allowBlank) { return undefined; } // We are valid
        }

        let pattern = '';
        if (typeof allowedChars !== 'undefined') {
            pattern = '^[' + allowedChars + ']+$'; // TODO: SHOULD PROLLY ESCAPE THE SCARY ONES?
        } else {
            pattern = '^-?(\\d+|\\d{1,3}(,\\d{3})*)';
            if (allowDecimal === true) {
                pattern += '(\\.\\d+)?';
            }
            pattern += '$';
        }

        error = this.validateString(inputNumber, inputName, undefined, undefined, pattern);

        if (!error) {
            inputNumber = Number((inputNumber + '').replace(/,/g, ''));
            if (allowDecimal !== true) { inputNumber = Math.round(Number(inputNumber)); }

            if (!error && (isNaN(inputNumber) || !isFinite(inputNumber))) {
                error = inputName + ' is not a valid number';
            }
            if (!error && typeof minNumber !== 'undefined') {
                if (typeof minNumber === 'string') {
                    // Parameter value
                    const key = minNumber;
                    if (params && typeof params === 'object' && params.hasOwnProperty(key) && inputNumber < params[key]) {
                        error = inputName + ' cannot be less than ' + RSValidatorService.formatNumber(params[key], 0, '.', ',')  + '';
                    }

                } else {
                    if (inputNumber < minNumber) {
                        error = inputName + ' cannot be less than ' + RSValidatorService.formatNumber(minNumber, 0, '.', ',')  + '';
                    }
                }
            }

            if (!error && typeof maxNumber !== 'undefined') {
                if (typeof maxNumber === 'string') {
                    // Parameter value
                    const key = maxNumber;
                    if (params && typeof params === 'object' && params.hasOwnProperty(key) && inputNumber > params[key]) {
                        error = inputName + ' cannot be more than ' + RSValidatorService.formatNumber(params[key], 0, '.', ',') + '';
                    }
                } else {
                    if (inputNumber > maxNumber) {
                        error = inputName + ' cannot be more than ' + RSValidatorService.formatNumber(maxNumber, 0, '.', ',') + '';
                    }
                }
            }
        }

        return error;
    }

    // Provide isValidTypeOf
    // undefined === no error!
    static invalidTypeOf(value: string | number | boolean, type: string, nicename?: string, validatorParamters?: object): string {
        let error: string;
        if (!type) { return; }

        const profile: RSValidator.Field = this.getValidationProfile(type);
        if (typeof nicename === 'undefined') {
            nicename = type.charAt(0).toUpperCase() + type.slice(1);
            nicename = nicename.replace(/_/, ' ');
        }
        if (typeof validatorParamters !== 'object') { validatorParamters = {}; }
        if (typeof profile !== 'undefined') {
            if (profile.type === 's') {
                error = this.validateString(value, nicename, profile.min, profile.max, profile.regex.pattern, profile.regex.flags, profile.allowedCharacters, profile.delim, profile.delim_limit, validatorParamters);
            } else if (profile.type === 'n') {
                error = this.validateNumber(value, nicename, profile.min, profile.max, profile.decimals, profile.allowedCharacters, profile.allowBlank, validatorParamters);
            } else if (profile.type === 'r') {
                error = this.valueMissing(value, nicename);
            } else if (profile.type === 'a') {
                error = this.validateArray(value, nicename, profile.min, profile.max);
            } else {
                console.warn('RSValidator::: Unknown validation profile type: ' + profile.type);
            }
        } else {
            console.warn('RSValidator::: Unknown validation profile: ' + type);
        }

        // Override our auto generated error with a specific error;
        if (typeof error !== 'undefined' && typeof profile.error !== 'undefined') { error = profile.error; }
        return error;
    }

    static validateArray(inputArray?, inputName?, minLength?, maxLength?) {
        let error;
        if (typeof inputArray !== 'object' || !Array.isArray(inputArray)) {
            error = inputName + ' is not valid. Please try again.';
        } else {
            const iLength = inputArray.length;
            if (!error && typeof minLength !== 'undefined') {
                if (iLength < minLength) {
                    error = inputName + ' is not valid (too small). Please try again.';
                }
            }
            if (!error && typeof maxLength !== 'undefined') {
                if (iLength > maxLength) {
                    error = inputName + ' is not valid (too large). Please try again.';
                }
            }
        }
        return error;
    }

    static validateObject(_validation: RSValidator.ValidationMember[], object: object, stage: number = RSValidateObjectProgress.all): RSValidator.ErrorMember[] {
        const aErrors: RSValidator.ErrorMember[] = [];

        _validation.forEach((rule: RSValidator.ValidationMember) => {
            if (stage === RSValidateObjectProgress.all || rule.validationStage <= stage) {
                // Is this required?
                if (rule.required === true ||
                    typeof rule.required === 'undefined' ||
                    (rule.required && typeof rule.required === 'function' && rule.required(object, stage))) {
                    let sError: string | RSValidator.ErrorMember[];

                    let aValue: any = null;
                    if (object.hasOwnProperty(rule.key) || object.hasOwnProperty(rule.key + '_') || typeof object[rule.key] === 'number') {
                        try {
                            aValue = object[rule.key];
                        } catch (e) { aValue = null; }
                    }

                    // Validate the object
                    if (typeof rule.validator === 'string') {
                        sError = this.invalidTypeOf(aValue, rule.validator, rule.name);
                    } else if (typeof rule.validator === 'function') {
                        sError = rule.validator(object, rule.key);
                    }

                    if (sError && typeof sError === 'string') {
                        aErrors.push(new RSValidator.ErrorMember(sError, rule.validationStage));
                    } else if (Array.isArray(sError) && sError.length > 0) {
                        sError.map((error) => {
                            if (error instanceof RSValidator.ErrorMember) {
                                error.section = rule.validationStage;
                                aErrors.push(error);
                            } else {
                                aErrors.push(new RSValidator.ErrorMember(error, rule.validationStage));
                            }
                        });
                    }
                }

            }
        });
        return aErrors;
    }

    static formatNumber (number, decimals, dec_point, thousands_sep) {
        // http://kevin.vanzonneveld.net
        // +   original by: Jonas Raoni Soares Silva (http://www.jsfromhell.com)
        // +   improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
        // +     bugfix by: Michael White (http://getsprink.com)
        // +     bugfix by: Benjamin Lupton
        // +     bugfix by: Allan Jensen (http://www.winternet.no)
        // +    revised by: Jonas Raoni Soares Silva (http://www.jsfromhell.com)
        // +     bugfix by: Howard Yeend
        // +    revised by: Luke Smith (http://lucassmith.name)
        // +     bugfix by: Diogo Resende
        // +     bugfix by: Rival
        // +      input by: Kheang Hok Chin (http://www.distantia.ca/)
        // +   improved by: davook
        // +   improved by: Brett Zamir (http://brett-zamir.me)
        // +      input by: Jay Klehr
        // +   improved by: Brett Zamir (http://brett-zamir.me)
        // +      input by: Amir Habibi (http://www.residence-mixte.com/)
        // +     bugfix by: Brett Zamir (http://brett-zamir.me)
        // +   improved by: Theriault
        // +   improved by: Drew Noakes
        // *     example 1: number_format(1234.56);
        // *     returns 1: '1,235'
        // *     example 2: number_format(1234.56, 2, ',', ' ');
        // *     returns 2: '1 234,56'
        // *     example 3: number_format(1234.5678, 2, '.', '');
        // *     returns 3: '1234.57'
        // *     example 4: number_format(67, 2, ',', '.');
        // *     returns 4: '67,00'
        // *     example 5: number_format(1000);
        // *     returns 5: '1,000'
        // *     example 6: number_format(67.311, 2);
        // *     returns 6: '67.31'
        // *     example 7: number_format(1000.55, 1);
        // *     returns 7: '1,000.6'
        // *     example 8: number_format(67000, 5, ',', '.');
        // *     returns 8: '67.000,00000'
        // *     example 9: number_format(0.9, 0);
        // *     returns 9: '1'
        // *    example 10: number_format('1.20', 2);
        // *    returns 10: '1.20'
        // *    example 11: number_format('1.20', 4);
        // *    returns 11: '1.2000'
        // *    example 12: number_format('1.2000', 3);
        // *    returns 12: '1.200'
        const n = !isFinite(+number) ? 0 : +number;
        const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
        const sep = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep;
        const dec = (typeof dec_point === 'undefined') ? '.' : dec_point;
        const toFixedFix = (x, precision) => {
            // Fix for IE parseFloat(0.55).toFixed(0) = 0;
            const k = Math.pow(10, precision);
            return Math.round(x * k) / k;
        };
        const s = (prec ? toFixedFix(n, prec) : Math.round(n)).toString().split('.');
        if (s[0].length > 3) {
            s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
        }
        if ((s[1] || '').length < prec) {
            s[1] = s[1] || '';
            s[1] += new Array(prec - s[1].length + 1).join('0');
        }
        return s.join(dec);
    }

    // Parse values from our JSON template;
    public parseRules(fields: object, regex: object): RSValidator.Rules {
        const rules = { fields: {}, regex: {} } as RSValidator.Rules;

        // Pull out fields and format
        for (const key in fields) {
            if (fields.hasOwnProperty(key)) {
                // Cast the value
                rules.fields[key] = fields[key] as RSValidator.Field;
            }
        }

        // Pull out regex and format
        for (const key in regex) {
            if (regex.hasOwnProperty(key)) {
                // Cast the value
                rules.regex[key] = regex[key] as RSValidator.FieldRegex;
            }
        }
        return rules;
    }

    // Provide setRules
    public setRules(rules: RSValidator.Rules) {
        RSValidatorService.rules = rules;
    }
}
